import {monotoneSegments} from './financialInterpolation';

const IDS=['hudson','bmi'];
const complete=p=>p&&IDS.every(id=>Number.isFinite(p.companies?.[id]));
const controls=curve=>[curve.from.y,curve.c1.y,curve.c2.y,curve.to.y];
const curves=(points,value)=>monotoneSegments(points.map(p=>({x:p.x,y:value(p)})))[0]?.curves||[];

// Add zero boundaries only for rendering. Reported points and amounts stay
// untouched; signed bands never smooth across unavailable source coverage.
function signedGroups(points){
 const runs=[];let run=[];
 for(const p of points){if(complete(p))run.push(p);else{if(run.length>1)runs.push(run);run=[];}}
 if(run.length>1)runs.push(run);
 const groups=[];
 for(const source of runs){
  const expanded=[source[0]];
  for(let i=0;i<source.length-1;i++){
   const a=source[i],b=source[i+1],cuts=IDS.filter(id=>a.companies[id]*b.companies[id]<0).map(id=>-a.companies[id]/(b.companies[id]-a.companies[id])).sort((a,b)=>a-b);
   for(const t of cuts.filter((t,i)=>i===0||t-cuts[i-1]>1e-10))expanded.push({x:a.x+(b.x-a.x)*t,companies:Object.fromEntries(IDS.map(id=>{const value=a.companies[id]+(b.companies[id]-a.companies[id])*t;return [id,Math.abs(value)<1e-8?0:value];}))});
   expanded.push(b);
  }
  let group=null;
  for(let i=0;i<expanded.length-1;i++){
   const a=expanded[i],b=expanded[i+1],pattern=IDS.map(id=>(a.companies[id]+b.companies[id])/2<0?'-':'+').join('');
   if(!group||group.pattern!==pattern){group={pattern,points:[a,b]};groups.push(group);}else group.points.push(b);
  }
 }
 return groups;
}

// Use the normal chart's monotone cubic curves for the outer total and the
// internal company boundary. Restrict the shared boundary's control handles to
// the companies' actual endpoint ranges. This keeps the total's normal curve,
// makes every band non-crossing, and preserves exact stacking between samples.
export function companyAreaRuns(points){
 return signedGroups(points).flatMap((group,groupIndex)=>{
  const hudson=curves(group.points,p=>p.companies.hudson),bmi=curves(group.points,p=>p.companies.bmi),sameSign=group.pattern[0]===group.pattern[1],total=sameSign?curves(group.points,p=>p.companies.hudson+p.companies.bmi):null;
  const segments={hudson:[],bmi:[]};
  for(let i=0;i<hudson.length;i++){
   const h=controls(hudson[i]),b=controls(bmi[i]),t=total?controls(total[i]):null;
   if(t){
    const hLow=Math.min(h[0],h[3]),hHigh=Math.max(h[0],h[3]),bLow=Math.min(b[0],b[3]),bHigh=Math.max(b[0],b[3]);
    for(const k of [1,2]){const low=Math.max(hLow,t[k]-bHigh),high=Math.min(hHigh,t[k]-bLow);h[k]=Math.max(low,Math.min(high,h[k]));}
   }
   const x0=hudson[i].from.x,x1=hudson[i].to.x,zero=[0,0,0,0];
   segments.hudson.push({x0,x1,from:zero,to:h});
   segments.bmi.push({x0,x1,from:t?h:zero,to:t||b});
  }
  return IDS.map((id,index)=>({key:`${groupIndex}:${id}`,id,sign:group.pattern[index]==='-'?'negative':'positive',segments:segments[id]}));
 });
}

export function companyAreaPaths(segments,y){
 if(!segments.length)return {area:'',line:''};
 const first=segments[0],last=segments.at(-1),forward=s=>` C${s.x0+(s.x1-s.x0)/3},${y(s.to[1])} ${s.x1-(s.x1-s.x0)/3},${y(s.to[2])} ${s.x1},${y(s.to[3])}`;
 const line=`M${first.x0},${y(first.to[0])}`+segments.map(forward).join('');
 const back=[...segments].reverse().map(s=>` C${s.x1-(s.x1-s.x0)/3},${y(s.from[2])} ${s.x0+(s.x1-s.x0)/3},${y(s.from[1])} ${s.x0},${y(s.from[0])}`).join('');
 return {line,area:line+` L${last.x1},${y(last.from[3])}`+back+' Z'};
}
